/**
 * The client apps that open AutomaDoc sessions outside the company portal — the Excel and
 * Word add-ins, the Google Sheets add-on, the Teams app (design 40-client-apps-design.md
 * §1.3, §6.1). Types shared by slices A (sessions), B (Microsoft), C (Google) and E
 * (source routes).
 */
import type { Platform } from './identity'

/** Which client opened a session (auth_sessions.client; NULL there means the portal). */
export const CLIENT_KINDS = ['excel', 'word', 'sheets', 'teams'] as const
export type ClientKind = (typeof CLIENT_KINDS)[number]

export function isClientKind(value: unknown): value is ClientKind {
  return typeof value === 'string' && (CLIENT_KINDS as readonly string[]).includes(value)
}

/** What a client gets back from its token exchange or its server-held sign-in. */
export interface ClientSessionResponse {
  /** AutomaDoc JWT: plat, sid, 24 h. */
  token: string
  /** Unix ms. */
  expiresAt: number
  user: { id: string; name: string; email?: string }
  tenant: { key: string; name: string; platform: Platform; isNew: boolean }
  isAdmin: boolean
}

export type ClientEnvironment = 'staging' | 'production'

/**
 * The static hosts per environment. Environments share nothing (owner rule): staging's
 * clients redirect only to staging hosts, production's only to production hosts. Teams
 * reuses the company portal's host.
 */
const CLIENT_HOSTS: Record<ClientEnvironment, { office: string; sheets: string; portal: string }> = {
  staging: {
    office: 'staging-office.automadoc.cc',
    sheets: 'staging-sheets.automadoc.cc',
    portal: 'staging-company.automadoc.cc',
  },
  production: {
    office: 'office.automadoc.cc',
    sheets: 'sheets.automadoc.cc',
    portal: 'company.automadoc.cc',
  },
}

const DONE_PATH = '/auth/done.html'

/** The API's host per environment (never a client redirect, so not in CLIENT_HOSTS). */
const API_HOSTS: Record<ClientEnvironment, string> = {
  staging: 'staging-api.automadoc.cc',
  production: 'api.automadoc.cc',
}

/**
 * `https://<host>` of each AutomaDoc host in an environment: the add-ins' static host, the
 * company portal and the API. For pages that link across hosts without an env variable of
 * their own (the docs site, a shared component): the environment alone picks them, so a
 * staging page can never name a production host.
 */
export function clientOrigins(environment: ClientEnvironment): { office: string; portal: string; api: string } {
  const hosts = CLIENT_HOSTS[environment]
  return { office: `https://${hosts.office}`, portal: `https://${hosts.portal}`, api: `https://${API_HOSTS[environment]}` }
}

/** The API's host name in an environment (`api.automadoc.cc`), e.g. for an Application ID URI. */
export function apiHostOf(environment: ClientEnvironment): string {
  return API_HOSTS[environment]
}

/**
 * Every redirect URI a server-held attempt may name, per environment. Each must be
 * registered as a **Web** redirect URI on that environment's Entra app and as an
 * authorised redirect URI on its Google web client.
 */
export const CLIENT_REDIRECT_URIS: Record<ClientEnvironment, readonly string[]> = {
  staging: Object.values(CLIENT_HOSTS.staging).map((host) => `https://${host}${DONE_PATH}`),
  production: Object.values(CLIENT_HOSTS.production).map((host) => `https://${host}${DONE_PATH}`),
}

/** The redirect URI a client's attempt uses: the add-ins' host, the add-on's, or the portal's for Teams. */
export function clientRedirectUri(environment: ClientEnvironment, client: ClientKind): string {
  const hosts = CLIENT_HOSTS[environment]
  const host = client === 'excel' || client === 'word' ? hosts.office : client === 'sheets' ? hosts.sheets : hosts.portal
  return `https://${host}${DONE_PATH}`
}

/**
 * The environment a Worker's ENVIRONMENT var names, for choosing redirect URIs. Anything
 * that is not production — development included — uses staging's hosts, so a local Worker
 * can never send a provider to a production page.
 */
export function clientEnvironmentOf(environment: string | undefined): ClientEnvironment {
  return environment === 'production' ? 'production' : 'staging'
}

// =============================================================================
// Client sign-in and connection routes (design 40 §6.1, §6.2): one set for both providers.
// handlers/microsoftAuth.ts and handlers/googleAuth.ts answer them; google.ts and
// microsoft.ts name a provider's variants as aliases or narrowings of these, never copies.
// =============================================================================

/** POST /api/auth/microsoft/client-token — an NAA access token for our own API (`access_as_user`). */
export interface MicrosoftClientTokenRequest {
  accessToken: string
  client: Extract<ClientKind, 'excel' | 'word' | 'teams'>
  locale?: string
}

/** POST /api/auth/{microsoft,google}/client-start — a server-held sign-in attempt. */
export interface ClientStartRequest {
  client: ClientKind
  intent: 'signin'
}

/** The authorize URL a client opens in its dialog or popup; `state` inside it is server-held. */
export interface ClientAuthorizeResponse {
  authorizeUrl: string
  /** Unix ms: when the attempt can no longer be completed. */
  expiresAt: number
}

/**
 * POST /api/auth/{microsoft,google}/client-complete — what the provider sent back to
 * `/auth/done.html`, posted as it came (an `error` too, so the API can map a refusal such as
 * AADSTS65001 to MS_CONSENT_REQUIRED with the admin-consent link).
 */
export interface ClientCompleteRequest {
  code?: string
  state: string
  error?: string
  errorDescription?: string
}

/**
 * client-complete for a `connect` attempt (a signin attempt answers ClientSessionResponse).
 * Microsoft's is MicrosoftConnectedResponse, Google's GoogleConnectCompleteResponse (which
 * may add a Picker grant). A connect started from a client is
 * `POST /api/tenant/connections/<provider>` with `client` (MicrosoftConnectRequest,
 * GoogleConnectRequest), answered by ClientAuthorizeResponse.
 */
export interface ClientConnectResult {
  connected: true
  purpose: string
  scopes: string[]
}
